import React, {Fragment, useMemo} from 'react';

import {I18nFunction} from 'i18n';
import {AppContext} from 'models/app';
import {transformServicesData} from 'units/services/utils';
import {transformSolutionsData} from 'units/solutions/utils';
import {NavigationSectionType} from 'units/navigation/models';

import EnvContext from 'contexts/EnvContext';
import NonceContext from 'contexts/NonceContext';
import RouterContext from 'contexts/RouterContext';
import UserContext from 'contexts/UserContext';
import LocaleContext from 'contexts/LocaleContext';
import LangContext from 'contexts/LangContext';
import MobileContext from 'contexts/MobileContext';
import TabletContext from 'contexts/TabletContext';
import CmntContext from 'contexts/CmntContext';
import IAMContext from 'contexts/IAMContext';
import YMapsContext from 'contexts/YMapsContext';
import ServicesDataContext from 'contexts/ServicesDataContext';
import TranslationContext from 'contexts/TranslationContext';
import BannerEventContext from 'contexts/BannerEventContext';
import SpeechkitConfigContext from 'contexts/SpeechkitConfigContext';
import useWindowBreakpoint from 'hooks/useWindowBreakpoint';
import BreakpointContext from 'contexts/BreakpointContext';
import SolutionsDataContext from 'contexts/SolutionsDataContext';
import FormsDataContext from 'contexts/FormsDataContext';
import FeatureFlagsContext from 'contexts/FeatureFlagsContext';
import DeviceContext from 'contexts/DeviceContext';

export interface ContextProviderProps {
    context: AppContext;
    i18n: I18nFunction;
}

const ContextProvider: React.FunctionComponent<ContextProviderProps> = (props) => {
    const {
        children,
        context: {
            router,
            locale,
            isMobile,
            isTablet,
            user,
            iam,
            nonce,
            env,
            cmntContext,
            yMapsContext,
            featureFlags,
            servicesData,
            solutionsData,
            formsData,
            bannerEvents,
            speechkitConfig,
            device,
        },
        i18n,
    } = props;

    const transformedServicesData = useMemo(
        () => transformServicesData(servicesData),
        [servicesData],
    );
    const transformedSolutionsData = useMemo(
        () =>
            transformSolutionsData(solutionsData, (key: string) =>
                i18n(NavigationSectionType.Solutions, key),
            ),
        [solutionsData, i18n],
    );

    /* eslint-disable react/jsx-key */
    const context = [
        <SpeechkitConfigContext.Provider value={speechkitConfig} />,
        <BannerEventContext.Provider value={bannerEvents} />,
        <YMapsContext.Provider value={yMapsContext} />,
        <CmntContext.Provider value={cmntContext} />,
        <TabletContext.Provider value={isTablet} />,
        <MobileContext.Provider value={isMobile} />,
        <LangContext.Provider value={locale.lang} />,
        <TranslationContext.Provider value={{i18n}} />,
        <LocaleContext.Provider value={locale} />,
        <IAMContext.Provider value={iam} />,
        <UserContext.Provider value={user} />,
        <RouterContext.Provider value={router?.getParams ? router?.getParams() : router} />,
        <NonceContext.Provider value={nonce} />,
        <EnvContext.Provider value={env} />,
        <FeatureFlagsContext.Provider value={featureFlags} />,
        <ServicesDataContext.Provider value={transformedServicesData} />,
        <SolutionsDataContext.Provider value={transformedSolutionsData} />,
        <FormsDataContext.Provider value={formsData} />,
        <BreakpointContext.Provider value={useWindowBreakpoint()} />,
        <DeviceContext.Provider value={{device}} />,
    ].reduceRight((prev, provider) => React.cloneElement(provider, {}, prev), children);
    /* eslint-enable react/jsx-key */

    return <Fragment>{context}</Fragment>;
};

export default ContextProvider;
